import React, { useEffect, useMemo, useState } from 'react';
import { CollapseBlock, MainTitle, Spinner } from '../../../common/index.jsx';
import GeneralPicker from '../../../common/form/basicFields/GeneralPicker/GeneralPicker.jsx';
import { useTranslation } from 'react-i18next';
import { useGetStudyProgramTypesQuery } from '../../../../redux/services/study_program_types.jsx';
import { useGetStudyProgramsQuery } from '../../../../redux/services/study_programs.jsx';
import { useGetStudySubjectsWsQuery } from '../../../../redux/services/study_subjects_ws.jsx';
import StudyPlansTable from './StudyPlansTable.jsx';
import { useParams } from 'react-router-dom';

const StudyPlansPage = () => {
  const { t } = useTranslation();

  const { shortcut: shortcutParam, year: yearParam } = useParams();
  const { data: studySubjectsWs, loading: isLoadingStudySubjects } =
    useGetStudySubjectsWsQuery();
  const { data: studyPrograms, loading: isLoadingStudyPrograms } =
    useGetStudyProgramsQuery();
  const { data: studyProgramTypes, loading: isLoadingStudyProgramTypes } =
    useGetStudyProgramTypesQuery();

  const STUDY_YEARS = {
    1: ['1', '2', '3'],
    2: ['1', '2'],
    3: ['1', '2', '3'],
    4: ['1', '2', '3'],
    5: ['1', '2'],
    6: ['1', '2', '3'],
  };

  const [selectedYear, setSelectedYear] = useState(null); // Default to first year
  const [selectedProgram, setSelectedProgram] = useState(null); // Default to informatika
  const [selectedProgramType, setSelectedProgramType] = useState(null); // Default Bc
  const [filteredPrograms, setFilteredPrograms] = useState([]);
  const [filteredYears, setFilteredYears] = useState([]);

  const studyProgramsByType = useMemo(() => {
    if (!studyPrograms) return {};
    return studyPrograms.reduce((acc, program) => {
      if (!acc[program.study_program_type_id])
        acc[program.study_program_type_id] = [];
      acc[program.study_program_type_id].push(program);
      return acc;
    }, {});
  }, [studyPrograms]);

  const studyProgramTypesOptions = useMemo(
    () =>
      studyProgramTypes?.map(({ id, name }) => ({
        value: id,
        name: name,
      })) || [],
    [studyProgramTypes],
  );

  useEffect(() => {
    if (selectedProgramType) {
      const programs = studyProgramsByType[selectedProgramType.value] || [];
      setFilteredPrograms(programs);
      setFilteredYears(STUDY_YEARS[selectedProgramType.value]);
      // Set selectedProgram to first program with matching study_program_type_id only if not already set or not in filteredPrograms
      if (
        programs.length > 0 &&
        (!selectedProgram ||
          !programs.some((p) => p.id === selectedProgram.value))
      ) {
        setSelectedProgram({ value: programs[0].id, name: programs[0].name });
      }
      // If selectedProgram is valid, keep it
    } else {
      setFilteredPrograms([]);
      setFilteredYears([]);
      setSelectedProgram(null);
    }
  }, [selectedProgramType, studyProgramsByType, selectedProgram]);

  const filterData = (semester) => {
    return studySubjectsWs?.filter(
      (subject) =>
        subject.study_program_id === selectedProgram?.value &&
        subject.school_year === selectedYear?.value &&
        subject.semester === semester,
    );
  };

  const studyProgramsOptions = useMemo(
    () => [
      ...filteredPrograms.map(({ id, name }) => ({
        value: id,
        name,
      })),
    ],
    [filteredPrograms, t],
  );

  const yearsOptions = useMemo(
    () => [
      ...filteredYears.map((year) => ({
        value: year,
        name: year.toString(),
      })),
    ],
    [filteredYears, t],
  );

  const [initialized, setInitialized] = useState(false);

  // Reset initialized when shortcutParam or yearParam changes
  useEffect(() => {
    setInitialized(false);
  }, [shortcutParam, yearParam]);

  // Parse shortcut and year from route params
  useEffect(() => {
    if (studyProgramTypesOptions?.length && studyPrograms && !initialized) {
      let shortcut = 'INF';
      let year = '1';
      if (typeof shortcutParam !== 'undefined' && shortcutParam) {
        shortcut = shortcutParam.toUpperCase();
      }
      if (typeof yearParam !== 'undefined' && yearParam) {
        year = yearParam;
      }
      // Find program by shortcut (case-insensitive)
      const matchingProgram = studyPrograms.find(
        (p) => p.shortcut?.toUpperCase() === shortcut,
      );
      if (matchingProgram) {
        const matchingProgramType = studyProgramTypesOptions.find(
          (type) => type.value === matchingProgram.study_program_type_id,
        );
        if (matchingProgramType) {
          setSelectedProgramType(matchingProgramType);
        }
        const matchingProgramOption = {
          value: matchingProgram.id,
          name: matchingProgram.name,
        };
        setSelectedProgram(matchingProgramOption);
      } else {
        // If shortcut not found, set to program with shortcut 'INF', else first with study_program_type_id = 1
        const infProgram = studyPrograms.find(
          (p) => p.shortcut?.toUpperCase() === 'INF',
        );
        if (infProgram) {
          setSelectedProgram({ value: infProgram.id, name: infProgram.name });
        } else {
          const firstBcProgram = studyPrograms.find(
            (p) => p.study_program_type_id === 1,
          );
          if (firstBcProgram) {
            setSelectedProgram({
              value: firstBcProgram.id,
              name: firstBcProgram.name,
            });
          } else {
            setSelectedProgram(null);
          }
        }
      }
      setSelectedYear({ value: year, name: year });
      setInitialized(true);
    }
  }, [
    studyProgramTypesOptions,
    studyPrograms,
    shortcutParam,
    yearParam,
    initialized,
  ]);

  // Remove automatic reset of selectedProgramType after it is set from URL
  useEffect(() => {
    if (
      studyProgramTypesOptions.length > 0 &&
      initialized &&
      !selectedProgramType
    ) {
      setSelectedProgramType(studyProgramTypesOptions[0]);
    }
  }, [studyProgramTypesOptions, initialized, selectedProgramType]);

  // Remove automatic reset of selectedProgram after it is set from URL
  useEffect(() => {
    if (studyProgramsOptions.length > 0 && initialized && !selectedProgram) {
      setSelectedProgram(studyProgramsOptions[0]);
    }
  }, [studyProgramsOptions, initialized, selectedProgram]);

  // Remove automatic reset of selectedYear after it is set from URL
  useEffect(() => {
    if (yearsOptions.length > 0 && initialized) {
      // Check if selectedYear is valid
      const validYears = yearsOptions.map((y) => y.value);
      if (!selectedYear || !validYears.includes(selectedYear.value)) {
        setSelectedYear(yearsOptions[0]);
      }
    }
  }, [yearsOptions, initialized, selectedYear]);

  useEffect(() => {
    const title = selectedProgram
      ? t('Študijný program {{name}}', { name: selectedProgram.name })
      : t('Študijné plány');
    if (title) {
      document.title = title;
    }
  }, [t, selectedProgram?.name]);

  return isLoadingStudySubjects ||
    isLoadingStudyPrograms ||
    isLoadingStudyProgramTypes ? (
    <Spinner />
  ) : (
    <>
      <MainTitle
        title={
          selectedProgram
            ? t('Študijný program {{name}}', { name: selectedProgram.name })
            : t('Študijné plány')
        }
      />
      <CollapseBlock collapsible={true} title={t('Filtre')}>
        <GeneralPicker
          value={selectedProgramType}
          options={studyProgramTypesOptions}
          label={t('Forma štúdia')}
          onChange={(value) => {
            setSelectedProgramType(value);
            setSelectedProgram(null);
          }}
        />
        <GeneralPicker
          value={selectedProgram}
          options={studyProgramsOptions}
          label={'Študijné programy'}
          onChange={(value) => {
            setSelectedProgram(value);
          }}
        />
        <GeneralPicker
          value={selectedYear}
          options={yearsOptions}
          label={'Ročník'}
          onChange={(value) => {
            setSelectedYear(value);
          }}
        />
      </CollapseBlock>

      <CollapseBlock collapsible={true} title={t('Zimný semester')}>
        <StudyPlansTable filteredSubjects={filterData('Z')}></StudyPlansTable>
      </CollapseBlock>
      <CollapseBlock collapsible={true} title={t('Letný semester')}>
        <StudyPlansTable filteredSubjects={filterData('L')}></StudyPlansTable>
      </CollapseBlock>
    </>
  );
};
export default StudyPlansPage;
